// ColumnPoolPage — the AC author's first selection surface.
//
// Per drafts/coframe_workbench_redesign_v0_1.md §4: a flat one-row-per-
// column grid spanning every table in the bound datasource, with a
// glob/substring search box at the top and multi-select checkboxes. The
// selection persists on the session (column_pool) and feeds the
// redesigned Declare page.
//
// Replaces the slice-3 TableExplorerPage in the sidebar nav. The old
// component file is kept on disk but no longer routed — see
// WorkbenchLayout.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import type { ColumnRef, ColumnView } from "../api/types";
import { useSession } from "./SessionContext";

export function ColumnPoolPage() {
  const { client, session, refreshSession } = useSession();

  // All columns the backend can see (one row per column across all tables).
  const [columns, setColumns] = useState<ColumnView[] | null>(null);
  // Which columns are currently selected — keyed by `db_schema|table|column`.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  // Which tables are currently included in the displayed grid. Empty set
  // is the "not initialised yet" sentinel; after the columns load it
  // defaults to "all tables". Local-only display filter — does NOT
  // persist on the session, and does NOT remove already-selected
  // columns from the pool (it just hides them in the grid).
  const [shownTables, setShownTables] = useState<Set<string>>(new Set());
  // Live search filter (glob: `*rev*`).
  const [search, setSearch] = useState<string>("");
  // Loading + error state.
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // Load columns from the server's cache. The GET /columns endpoint
  // returns the session's persisted column views without re-profiling
  // when the scope is unchanged (cached: true); pass refresh=true to
  // force a re-profile. This replaces the old "ListAllColumns on every
  // mount" behaviour — profiling is a full-table scan, so we only pay
  // it when the user (re)starts the Lattice or hits Refresh.
  const loadColumns = useCallback(
    async (opts: { refresh?: boolean } = {}) => {
      if (session === null) return;
      if (session.scope_tables.length === 0) {
        setColumns([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const r = await client.getColumns(session.session_id, opts);
        setColumns(r.columns);
        setShownTables(new Set(r.columns.map((c) => c.table)));
      } catch (e: unknown) {
        setError(formatError(e));
      } finally {
        setLoading(false);
      }
    },
    [client, session],
  );

  // On mount (or when session.scope_tables changes), fetch columns for
  // the in-scope tables only — using the server cache (no refresh). Seed
  // selection from the persisted pool.
  useEffect(() => {
    if (session === null) {
      setColumns(null);
      setSelectedKeys(new Set());
      return;
    }
    setSelectedKeys(new Set(session.column_pool.map((c) => refKey(c))));
    if (session.scope_tables.length === 0) {
      // No scope picked — render the empty state, don't even ask the
      // server. Saves the full-table-scan cost.
      setColumns([]);
      return;
    }
    void loadColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session_id, session?.scope_tables.join("|")]);

  // All distinct tables across the column list — sorted for stable
  // chip order. Computed once per columns-load.
  const allTables = useMemo(() => {
    if (columns === null) return [];
    return Array.from(new Set(columns.map((c) => c.table))).sort();
  }, [columns]);

  // Live-filter columns. Two filters in series:
  //   1. table-membership (the chip toggles at the top)
  //   2. glob/substring pattern from the search box
  const filtered = useMemo(() => {
    if (columns === null) return null;
    let result = columns;
    if (shownTables.size < allTables.length) {
      result = result.filter((c) => shownTables.has(c.table));
    }
    const pat = search.trim();
    if (pat !== "" && pat !== "*") {
      result = result.filter((c) => matchesPattern(c, pat));
    }
    return result;
  }, [columns, allTables, shownTables, search]);

  // Group the filtered columns by their physical table, preserving the
  // sorted table order. Each group renders as its own displayed table
  // (per Huayin's redesign — table name is the section heading, not a
  // per-row column).
  const filteredByTable = useMemo(() => {
    if (filtered === null) return [];
    const byTable = new Map<string, ColumnView[]>();
    for (const c of filtered) {
      const arr = byTable.get(c.table);
      if (arr) arr.push(c);
      else byTable.set(c.table, [c]);
    }
    return Array.from(byTable.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([table, rows]) => ({ table, rows }));
  }, [filtered]);

  function toggleTable(table: string) {
    setShownTables((prev) => {
      const next = new Set(prev);
      if (next.has(table)) next.delete(table);
      else next.add(table);
      return next;
    });
  }

  function showAllTables() {
    setShownTables(new Set(allTables));
  }

  function showNoTables() {
    setShownTables(new Set());
  }

  function toggleOne(c: ColumnView) {
    const key = refKey(c);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSavedMessage(null);
  }

  function toggleAllFiltered() {
    if (filtered === null) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      // If every filtered row is already selected, unselect them all.
      // Otherwise, select every filtered row.
      const allSelected = filtered.every((c) => next.has(refKey(c)));
      if (allSelected) {
        for (const c of filtered) next.delete(refKey(c));
      } else {
        for (const c of filtered) next.add(refKey(c));
      }
      return next;
    });
    setSavedMessage(null);
  }

  async function saveSelection() {
    if (session === null || columns === null) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    // Build the column refs from the selectedKeys + the columns list.
    const byKey = new Map<string, ColumnView>();
    for (const c of columns) byKey.set(refKey(c), c);
    const refs: ColumnRef[] = [];
    for (const key of selectedKeys) {
      const c = byKey.get(key);
      if (c === undefined) continue; // stale key — drop
      refs.push({ db_schema: c.db_schema, table: c.table, column: c.column });
    }
    try {
      const r = await client.runOp(session.session_id, "SetColumnPool", {
        inputs: { columns: refs },
      });
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "SetColumnPool failed");
      }
      setSavedMessage(`Saved — ${refs.length} columns in pool.`);
      // Refresh session so sidebar / Declare page see the new pool.
      refreshSession();
    } catch (e: unknown) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  }

  if (session === null) {
    return (
      <section>
        <h1>Columns</h1>
        <p style={{ color: "#888" }}>
          Create a session on the <em>AC Session</em> tab to start picking
          columns for your AC.
        </p>
      </section>
    );
  }

  // No tables in scope yet → don't render the grid (and don't even
  // fetch). Profiling is full-table-scan-per-table; we want the user
  // to pick the tables first so we only pay for what they care about.
  if (session.scope_tables.length === 0) {
    return (
      <section>
        <h1>Columns</h1>
        <div
          style={{
            padding: "1.25rem 1.5rem",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            color: "#444",
            marginTop: "1rem",
          }}
        >
          <p style={{ margin: 0 }}>
            No tables in scope yet. Pick the tables this AC should cover
            on the <strong>AC Session</strong> tab — under{" "}
            <em>Tables of interest</em> — and save. This page then loads
            columns for those tables only (full-table profiling is
            expensive, so we scope it up front).
          </p>
        </div>
      </section>
    );
  }

  const totalCount = columns?.length ?? 0;
  const filteredCount = filtered?.length ?? 0;
  const selectedCount = selectedKeys.size;
  const persistedCount = session.column_pool_size;
  const dirty = !poolsEqual(
    selectedKeys,
    new Set(session.column_pool.map((c) => refKey(c))),
  );

  return (
    <section>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Columns</h1>
          <p style={subheaderStyle}>
            Pick the columns that constitute your AC. Each selected
            column becomes one row on the <em>Declare</em> page.
          </p>
        </div>
        <div style={statsStyle}>
          <Stat label="selected" value={String(selectedCount)} />
          <Stat
            label="persisted"
            value={String(persistedCount)}
            hint={dirty ? "Unsaved changes" : undefined}
          />
          <Stat
            label="showing"
            value={`${filteredCount} / ${totalCount}`}
          />
        </div>
      </header>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}
      {savedMessage && (
        <div style={savedBlockStyle}>{savedMessage}</div>
      )}

      {allTables.length > 0 && (
        <div style={tableFilterRowStyle}>
          <span style={tableFilterLabelStyle}>Tables:</span>
          {allTables.map((t) => {
            const on = shownTables.has(t);
            return (
              <button
                key={t}
                onClick={() => toggleTable(t)}
                style={on ? tableChipOnStyle : tableChipOffStyle}
                title={on ? `Hide ${t}'s columns` : `Show ${t}'s columns`}
              >
                {t}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <button
            onClick={showAllTables}
            style={tinyGhostButtonStyle}
            disabled={shownTables.size === allTables.length}
          >
            all
          </button>
          <button
            onClick={showNoTables}
            style={tinyGhostButtonStyle}
            disabled={shownTables.size === 0}
          >
            none
          </button>
        </div>
      )}

      <div style={controlsRowStyle}>
        <input
          type="text"
          placeholder="Search columns (glob: *rev*, region_*)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={searchInputStyle}
          autoFocus
        />
        <button
          onClick={() => setSearch("")}
          style={ghostButtonStyle}
          disabled={search === ""}
        >
          clear
        </button>
        <button
          onClick={toggleAllFiltered}
          style={ghostButtonStyle}
          disabled={filtered === null || filtered.length === 0}
          title="Toggle every currently-visible row"
        >
          select all (visible)
        </button>
        <button
          onClick={() => void loadColumns({ refresh: true })}
          style={ghostButtonStyle}
          disabled={loading}
          title="Re-profile the in-scope tables from the backend (ignores cache)"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={saveSelection}
          style={dirty ? primaryButtonStyle : primaryButtonDisabledStyle}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : dirty ? "Save pool" : "No changes"}
        </button>
      </div>

      {loading && (
        <p style={{ color: "#888" }}>Loading columns from backend…</p>
      )}

      {!loading &&
        filteredByTable.map(({ table, rows }) => (
          <TableSection
            key={table}
            table={table}
            rows={rows}
            selectedKeys={selectedKeys}
            onToggle={toggleOne}
          />
        ))}

      {!loading && filtered !== null && filtered.length === 0 && (
        <p style={{ color: "#888", marginTop: "1rem" }}>
          No columns match <code>{search}</code>.
        </p>
      )}
    </section>
  );
}

// ── Per-table section ─────────────────────────────────────────────────

// One displayed table per physical table. The table name and its size
// (row count) live in the section header — not as per-row columns —
// and the schema column is gone entirely (per Huayin's redesign).
function TableSection({
  table,
  rows,
  selectedKeys,
  onToggle,
}: {
  table: string;
  rows: ColumnView[];
  selectedKeys: Set<string>;
  onToggle: (c: ColumnView) => void;
}) {
  // Table size = row count. Every column in a table shares the same
  // n_rows, so read it off the first row.
  const rowCount = rows[0]?.n_rows ?? 0;
  return (
    <div style={tableSectionStyle}>
      <div style={tableSectionHeaderStyle}>
        <code style={tableSectionNameStyle}>{table}</code>
        <span style={tableSectionMetaStyle}>
          {rowCount.toLocaleString()} rows · {rows.length}{" "}
          {rows.length === 1 ? "column" : "columns"}
        </span>
      </div>
      <table style={gridStyle}>
        <thead>
          <tr>
            <th style={thCheckStyle}></th>
            <th style={thStyle}>column</th>
            <th style={thStyle}>dtype</th>
            <th style={thStyle}>kind</th>
            <th style={thStyleNumeric}>nunique</th>
            <th style={thStyleNumeric}>null</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const key = refKey(c);
            const checked = selectedKeys.has(key);
            return (
              <tr
                key={key}
                style={checked ? trCheckedStyle : trStyle}
                onClick={() => onToggle(c)}
              >
                <td style={tdCheckStyle}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(c)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td style={tdStyle}>
                  <code style={{ fontWeight: 500 }}>{c.column}</code>
                </td>
                <td style={tdStyle}>{c.dtype}</td>
                <td style={tdStyle}>
                  <span style={kindBadgeStyle(c.kind_hint)}>{c.kind_hint}</span>
                </td>
                <td style={tdNumericStyle}>{c.nunique.toLocaleString()}</td>
                <td style={tdNumericStyle}>
                  {(c.n_rows - c.n_non_null).toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function refKey(c: ColumnRef | ColumnView): string {
  return `${c.db_schema}|${c.table}|${c.column}`;
}

function matchesPattern(c: ColumnView, pattern: string): boolean {
  // Glob: `*` matches anything (inside the pattern). Case-insensitive.
  // We match against (db_schema, table, column) concatenated, plus the
  // bare column name, so `region_*` works on column or table, and
  // `transactions.revenue` (qualified) also matches.
  const lower = pattern.toLowerCase();
  const haystacks = [
    c.column.toLowerCase(),
    c.table.toLowerCase(),
    `${c.table}.${c.column}`.toLowerCase(),
    c.db_schema
      ? `${c.db_schema}.${c.table}.${c.column}`.toLowerCase()
      : "",
  ];
  // Quick path: substring (no glob chars).
  if (!lower.includes("*") && !lower.includes("?")) {
    return haystacks.some((h) => h.includes(lower));
  }
  // Glob: escape regex specials then translate * → .* and ? → .
  const re = new RegExp(
    "^" +
      lower
        .replace(/[.+^${}()|[\]\\]/g, "\\$&")
        .replace(/\*/g, ".*")
        .replace(/\?/g, ".") +
      "$",
  );
  return haystacks.some((h) => re.test(h));
}

function poolsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Small components ──────────────────────────────────────────────────

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div style={statStyle} title={hint}>
      <div style={statLabelStyle}>{label}</div>
      <div
        style={
          hint
            ? { ...statValueStyle, color: "#8b6914" }
            : statValueStyle
        }
      >
        {value}
        {hint ? " •" : ""}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "1.5rem",
  marginBottom: "1rem",
};
const subheaderStyle: React.CSSProperties = {
  color: "#666",
  margin: "0.25rem 0 0 0",
  fontSize: "0.9rem",
};
const statsStyle: React.CSSProperties = {
  display: "flex",
  gap: "1.25rem",
  paddingTop: "0.3rem",
};
const statStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const statLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const statValueStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 500,
};
const tableFilterRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.35rem",
  alignItems: "center",
  marginBottom: "0.6rem",
  padding: "0.5rem 0.6rem",
  background: "#fafafa",
  border: "1px solid #eee",
  borderRadius: 4,
};
const tableFilterLabelStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#666",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: 600,
  marginRight: "0.25rem",
};
const tableChipOnStyle: React.CSSProperties = {
  padding: "0.2rem 0.55rem",
  fontSize: "0.8rem",
  background: "#eef4ff",
  color: "#0366d6",
  border: "1px solid #cdddf2",
  borderRadius: 12,
  cursor: "pointer",
  fontFamily: "monospace",
};
const tableChipOffStyle: React.CSSProperties = {
  ...tableChipOnStyle,
  background: "#f4f4f4",
  color: "#888",
  border: "1px solid #ddd",
};
const tinyGhostButtonStyle: React.CSSProperties = {
  padding: "0.15rem 0.5rem",
  fontSize: "0.75rem",
  background: "#fff",
  color: "#444",
  border: "1px solid #cbd5e0",
  borderRadius: 3,
  cursor: "pointer",
};

const controlsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  marginBottom: "1rem",
};
const searchInputStyle: React.CSSProperties = {
  flex: "0 1 360px",
  padding: "0.4rem 0.6rem",
  fontSize: "0.95rem",
  border: "1px solid #cbd5e0",
  borderRadius: 4,
  fontFamily: "monospace",
};
const ghostButtonStyle: React.CSSProperties = {
  padding: "0.4rem 0.7rem",
  fontSize: "0.85rem",
  background: "#fff",
  color: "#444",
  border: "1px solid #cbd5e0",
  borderRadius: 4,
  cursor: "pointer",
};
const primaryButtonStyle: React.CSSProperties = {
  padding: "0.45rem 1rem",
  fontSize: "0.9rem",
  background: "#0366d6",
  color: "#fff",
  border: "1px solid #0366d6",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 500,
};
const primaryButtonDisabledStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: "#e0e0e0",
  borderColor: "#cbd5e0",
  color: "#888",
  cursor: "not-allowed",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
};
const savedBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#e6f7e6",
  border: "1px solid #a5d8b8",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
  color: "#1a6b3a",
};
const tableSectionStyle: React.CSSProperties = {
  marginBottom: "1.5rem",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  overflow: "hidden",
};
const tableSectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "0.75rem",
  padding: "0.5rem 0.75rem",
  background: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
};
const tableSectionNameStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#1c4e80",
};
const tableSectionMetaStyle: React.CSSProperties = {
  fontSize: "0.78rem",
  color: "#64748b",
  fontVariantNumeric: "tabular-nums",
};
const gridStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.45rem 0.6rem",
  background: "#fafafa",
  borderBottom: "2px solid #ddd",
  fontSize: "0.8rem",
  color: "#666",
  fontWeight: 500,
  position: "sticky",
  top: 0,
};
const thCheckStyle: React.CSSProperties = {
  ...thStyle,
  width: 30,
};
const thStyleNumeric: React.CSSProperties = {
  ...thStyle,
  textAlign: "right",
};
const trStyle: React.CSSProperties = {
  cursor: "pointer",
};
const trCheckedStyle: React.CSSProperties = {
  ...trStyle,
  background: "#eef4ff",
};
const tdStyle: React.CSSProperties = {
  padding: "0.35rem 0.6rem",
  borderBottom: "1px solid #eee",
};
const tdCheckStyle: React.CSSProperties = {
  ...tdStyle,
  width: 30,
};
const tdNumericStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

function kindBadgeStyle(kind: string): React.CSSProperties {
  const palette: Record<string, [string, string, string]> = {
    dimension: ["#e3edf9", "#1c4e80", "#bcd5f0"],
    measure: ["#e9f5e9", "#1a6b3a", "#bce0c1"],
    identifier: ["#f4ecf9", "#5c2780", "#d8c0e9"],
    timestamp: ["#fef2d6", "#8b6914", "#e6d8a8"],
    boolean: ["#fff0f0", "#a02020", "#f3c0c0"],
    text: ["#f0f0f0", "#555", "#ddd"],
    unknown: ["#f8f8f8", "#888", "#ddd"],
  };
  const [bg, fg, border] = palette[kind] ?? palette["unknown"]!;
  return {
    background: bg,
    color: fg,
    border: `1px solid ${border}`,
    padding: "0.1rem 0.4rem",
    borderRadius: 3,
    fontSize: "0.75rem",
  };
}

